import type { SkillWorkflowInput } from '@parcel/types';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import * as React from 'react';
import { getWorkspaceScopedPath } from '../components/app-sidebar-nav.js';
// The light context module: importing the runtime itself would pull the Spec chunk into this page's bundle.
import { useOptionalSpecFollow } from '../components/spec/follow-provider.js';
import { useOptionalSpecRuntime } from '../components/spec/runtime-context.js';
import { isSpecSurfaceVisible } from './spec-config.js';
import {
  defaultSpecContextStorage,
  specContextStorageKey,
  writeSpecConversationContext,
} from './spec-context-storage.js';
import { SPEC_CHAT_SEARCH_KEY, useRefreshSpecConversations } from './spec-conversations.js';
import { useSpecLaunchState } from './spec-launch.js';
import type { SpecContextFocus } from './spec.js';

export interface OpenSpecChatInput {
  /** The first message, sent as soon as the chat opens. */
  starter: string;
  /** Skills attached to that first message. */
  skills?: readonly string[];
  title?: string;
  /** Binds the chat to a hidden system workflow, the way Create skill binds. */
  skillWorkflow?: SkillWorkflowInput;
  /** The record the chat is about, attached to the first message. */
  context?: SpecContextFocus;
  /** Opens the chat in the panel beside this page rather than on Spec's own page. */
  beside?: boolean;
}

/** Opens a new Spec chat that sends `starter` itself; throws where Spec is not available. */
export function useOpenSpecChat(): (input: OpenSpecChatInput) => Promise<void> {
  const runtime = useOptionalSpecRuntime();
  const follow = useOptionalSpecFollow();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const refresh = useRefreshSpecConversations(runtime?.workspaceId);
  // A double click never creates two chats; a different chat still opens.
  const opening = React.useRef<{ key: string; run: Promise<void> } | null>(null);
  const open = React.useCallback(
    async ({ starter, skills, title, skillWorkflow, context, beside }: OpenSpecChatInput) => {
      const workspaceId = runtime?.workspaceId;
      if (!runtime || !workspaceId) throw new Error('Spec is not available in this workspace.');
      const created = await runtime.client.createConversation(
        workspaceId,
        title ?? 'New chat',
        skillWorkflow ? { skillWorkflow } : {},
      );
      if (context) {
        // The chat's chip holds the record, so later messages carry it as the starter does.
        const storage = defaultSpecContextStorage();
        const key = specContextStorageKey(workspaceId, created.id);
        if (storage && key) {
          writeSpecConversationContext(storage, key, {
            focus: { focus: context, label: title ?? null },
            connectionsOff: [],
            webOff: false,
          });
        }
        runtime.adoptStarter(created, starter, skills, context);
      } else runtime.adoptStarter(created, starter, skills);
      refresh();
      if (beside && follow) {
        follow.openPanel();
        return;
      }
      await navigate({
        to: getWorkspaceScopedPath(pathname, '/spec'),
        search: { [SPEC_CHAT_SEARCH_KEY]: created.id },
      } as never);
    },
    [follow, navigate, pathname, refresh, runtime],
  );
  return React.useCallback(
    (input: OpenSpecChatInput) => {
      const key = JSON.stringify(input);
      if (opening.current?.key === key) return opening.current.run;
      const run = open(input).finally(() => {
        if (opening.current?.run === run) opening.current = null;
      });
      opening.current = { key, run };
      return run;
    },
    [open],
  );
}

/** Whether a page can open a Spec chat here: Spec is shown (the Knowledge page's gate) and not read-only. */
export function useSpecChatAvailable(): boolean {
  const runtime = useOptionalSpecRuntime();
  const launchState = useSpecLaunchState();
  return (
    Boolean(runtime?.workspaceId) &&
    isSpecSurfaceVisible(launchState) &&
    launchState !== 'read_only'
  );
}
